/* ============================================================
 * <klaviyo-prerelease> - interest signup for frames in
 * pre-production testing (templates/product.pre-release-test.json).
 *
 * Mirrors klaviyo-bis.js but talks to two PUBLIC Klaviyo client
 * endpoints (public company id only, no script-order dependency):
 *   1. /client/subscriptions/  -> adds the email to the pre-order
 *      list (data-list-id) with email marketing consent.
 *   2. /client/events/         -> fires a "Pre-Release Signup" event
 *      with product / variant details so frames can be ranked by
 *      signup volume in Klaviyo.
 * Then:
 *   3. Meta pixel custom event "PreReleaseSignup", sent straight to
 *      Meta's pixel endpoint from the page (see trackMetaSignup).
 *      Only this one event is sent, so it never overlaps the events
 *      Aimerce sends through CAPI.
 *
 * Docs:
 *  https://developers.klaviyo.com/en/reference/create_client_subscription
 *  https://developers.klaviyo.com/en/reference/create_client_event
 * ============================================================ */
class KlaviyoPreRelease extends HTMLElement {
  constructor() {
    super();
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onEsc = this.onEsc.bind(this);
  }

  connectedCallback() {
    this.companyId = this.dataset.companyId;
    this.revision = this.dataset.revision || '2024-06-15';
    this.listId = this.dataset.listId;

    this.product = {
      id: this.dataset.productId,
      title: this.dataset.productTitle,
      handle: this.dataset.productHandle,
      url: this.dataset.productUrl,
      type: this.dataset.productType,
    };
    this.variant = {
      id: this.dataset.variantId,
      title: this.dataset.variantTitle,
      sku: this.dataset.variantSku,
    };

    this.trigger = this.querySelector('[data-prerelease-trigger]');
    this.modal = this.querySelector('[data-prerelease-modal]');
    this.submitBtn = this.querySelector('[data-prerelease-submit]');
    this.emailInput = this.querySelector('[data-prerelease-email]');
    this.message = this.querySelector('[data-prerelease-message]');

    if (this.trigger) this.trigger.addEventListener('click', () => this.openModal());
    this.querySelectorAll('[data-prerelease-close]').forEach((el) =>
      el.addEventListener('click', () => this.closeModal())
    );
    if (this.modal) {
      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal) this.closeModal();
      });
    }
    if (this.submitBtn) this.submitBtn.addEventListener('click', () => this.onSubmit());
    if (this.emailInput) {
      this.emailInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.onSubmit();
        }
      });
    }

    // Track the selected colorway so the event records the right variant.
    // constants.js declares PUB_SUB_EVENTS with const: a global binding, never a
    // window property, so window.PUB_SUB_EVENTS is always undefined.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
    }
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
    document.removeEventListener('keydown', this.onEsc);
  }

  onVariantChange(event) {
    if (this.dataset.sectionId && event?.data?.sectionId !== this.dataset.sectionId) return;
    const variant = event && event.data && event.data.variant;
    if (!variant) return;
    this.variant = {
      id: String(variant.id),
      title: variant.title || '',
      sku: variant.sku || '',
    };
  }

  onEsc(event) {
    if (event.key === 'Escape') this.closeModal();
  }

  ensureFont() {
    if (KlaviyoPreRelease.fontRequested) return;
    KlaviyoPreRelease.fontRequested = true;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@400;500;700&display=swap';
    document.head.appendChild(link);
  }

  openModal() {
    if (!this.modal) return;
    this.ensureFont();
    this.modal.removeAttribute('hidden');
    document.addEventListener('keydown', this.onEsc);
    requestAnimationFrame(() => {
      this.modal.classList.add('is-open');
      if (this.emailInput) this.emailInput.focus();
    });
  }

  closeModal() {
    if (!this.modal) return;
    this.modal.classList.remove('is-open');
    document.removeEventListener('keydown', this.onEsc);
    setTimeout(() => this.modal.setAttribute('hidden', ''), 200);
  }

  setState(state, messageText) {
    this.dataset.state = state;
    if (this.message && messageText != null) this.message.textContent = messageText;
    if (state !== 'error' && this.message) this.message.textContent = '';
  }

  async onSubmit() {
    const email = ((this.emailInput && this.emailInput.value) || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.setState('error', 'Please enter a valid email address.');
      if (this.emailInput) this.emailInput.focus();
      return;
    }
    if (!this.listId) {
      this.setState('error', 'Something went wrong. Please refresh and try again.');
      return;
    }

    this.setState('loading');

    try {
      const subscribed = await this.subscribeToList(email);
      if (!subscribed) {
        this.setState('error', 'Sorry, that didn’t go through. Please try again.');
        return;
      }
      // Secondary, non-fatal: the per-frame event used for demand ranking.
      await this.trackSignupEvent(email);
      // Secondary, non-fatal: Meta "PreReleaseSignup" custom event.
      this.trackMetaSignup(email);
      this.setState('success');
    } catch (error) {
      this.setState('error', 'Network error. Please try again.');
    }
  }

  // Klaviyo public client Subscriptions API. Adds the profile to the
  // pre-order list with email marketing consent. Returns true on success.
  async subscribeToList(email) {
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Pre-Release PDP: ' + (this.product.title || ''),
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: {
                  last_pre_release_signup: this.product.title || '',
                  last_pre_release_variant: this.variant.title || '',
                },
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
              },
            },
          },
        },
        relationships: { list: { data: { type: 'list', id: this.listId } } },
      },
    };

    const response = await fetch(
      'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(this.companyId),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: this.revision },
        body: JSON.stringify(payload),
      }
    );
    // Klaviyo returns 202 Accepted (empty body) on success.
    return response.ok || response.status === 202;
  }

  // Meta pixel custom event "PreReleaseSignup", sent straight to Meta's
  // pixel endpoint (facebook.com/tr) as a 1x1 image request. Meta's
  // fbevents.js loaded on this site but never sent the event, and the
  // Shopify custom pixel sandbox did not either, so this skips both.
  // Reuses the _fbp / _fbc cookies already on the site so Meta can tie the
  // signup to the ad click, and sends the email SHA-256 hashed (ud[em]).
  // Only this one event is sent, so nothing overlaps Aimerce CAPI.
  // Non-fatal.
  async trackMetaSignup(email) {
    try {
      const PIXEL_ID = '260912538101769';
      const readCookie = (name) => {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : '';
      };

      let emailHash = '';
      const normalized = (email || '').trim().toLowerCase();
      if (normalized && window.crypto && window.crypto.subtle && window.TextEncoder) {
        const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(normalized));
        emailHash = Array.from(new Uint8Array(digest))
          .map((byte) => byte.toString(16).padStart(2, '0'))
          .join('');
      }

      const params = new URLSearchParams({
        id: PIXEL_ID,
        ev: 'PreReleaseSignup',
        dl: window.location.href,
        rl: document.referrer || '',
        ts: String(Date.now()),
        eid: 'prerelease-' + (this.product.id || '') + '-' + Date.now(),
        'cd[content_name]': this.product.title || '',
        'cd[content_ids]': JSON.stringify([String(this.product.id || '')]),
        'cd[content_type]': 'product',
        'cd[product_handle]': this.product.handle || '',
        'cd[variant_id]': String(this.variant.id || ''),
      });
      const fbp = readCookie('_fbp');
      const fbc = readCookie('_fbc');
      if (fbp) params.set('fbp', fbp);
      if (fbc) params.set('fbc', fbc);
      if (emailHash) params.set('ud[em]', emailHash);

      const pixel = new Image(1, 1);
      pixel.src = 'https://www.facebook.com/tr?' + params.toString();
    } catch (error) {
      /* non-fatal */
    }
  }

  // Klaviyo public client Events API. Non-fatal.
  async trackSignupEvent(email) {
    const payload = {
      data: {
        type: 'event',
        attributes: {
          properties: {
            'Product Name': this.product.title || '',
            'Product ID': this.product.id || '',
            'Product Handle': this.product.handle || '',
            'Product Type': this.product.type || '',
            'Product URL': this.product.url || '',
            'Variant Title': this.variant.title || '',
            'Variant ID': this.variant.id || '',
            SKU: this.variant.sku || '',
            Source: 'Pre-Release PDP',
          },
          metric: { data: { type: 'metric', attributes: { name: 'Pre-Release Signup' } } },
          profile: { data: { type: 'profile', attributes: { email: email } } },
        },
      },
    };

    try {
      await fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(this.companyId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: this.revision },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      /* non-fatal */
    }
  }
}

if (!customElements.get('klaviyo-prerelease')) {
  customElements.define('klaviyo-prerelease', KlaviyoPreRelease);
}
